import { maybe, rd, RemoteData } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { useEffect, useMemo } from 'react';
import { ApiClient } from 'lib/api/ApiClient.types';
import { GROUP_ACTIONS } from 'store/v1/groups/groups.constants';
import { ReduxProxy } from 'v5/api/_common/reduxProxy';
import { enumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { searchUtils } from 'v5/api/_common/query/queryUtils';
import { Group } from '../../../../__types__/v1/models/Group';
import { ResourceType } from '../../../../__types__/v1/models/ResourceType';
import { GroupsQuery, GroupsService } from './GroupsService';
import { booleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import { WithServices } from 'v5/platform/ts/services';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';

export type GroupsSnapshot = {
  data: Record<number, Group>;
  fetched: boolean;
  fetching: boolean;
  fetchError?: unknown;
};

function resourceTypeIdToModelMap(
  resourceTypes: ResourceType[]
): Map<number, NonNullable<ResourceType['model']>> {
  const map = new Map<number, NonNullable<ResourceType['model']>>();
  for (const rt of resourceTypes) {
    if (rt.model != null) {
      map.set(rt.id, rt.model);
    }
  }
  return map;
}

function matchesQuery(
  query: GroupsQuery,
  group: Group,
  resourceTypeIdToModel: Map<number, NonNullable<ResourceType['model']>>
): boolean {
  if (!searchUtils.matches(query.search, group.name ?? '')) {
    return false;
  }
  if (
    !booleanFilter.matches(query.filters?.archived, Boolean(group.archived))
  ) {
    return false;
  }
  if (!enumFilter.matches(query.filters?.entity_type, group.entity_type)) {
    return false;
  }
  const resourceTypeId =
    group.entity_id == null ? group.entity_id : Number(group.entity_id);
  if (!enumFilter.matches(query.filters?.resource_type_id, resourceTypeId)) {
    return false;
  }
  if (maybe.isPresent(query.filters?.resource_type_model)) {
    const groupModel =
      resourceTypeId != null
        ? resourceTypeIdToModel.get(resourceTypeId)
        : undefined;
    if (!enumFilter.matches(query.filters.resource_type_model, groupModel)) {
      return false;
    }
  }
  return true;
}

export function createGroupsService({
  client,
  reduxProxy,
  services,
  useGroupsSnapshot,
  useResourceTypes
}: WithServices<[WithMessageService]> & {
  client: ApiClient;
  reduxProxy: ReduxProxy;
  useGroupsSnapshot: () => GroupsSnapshot;
  useResourceTypes: () => ResourceType[];
}): GroupsService {
  let inFlight: Promise<void> | undefined;

  // Legacy UI reads state.groups, so Redux holds both the data and the request state.
  async function load() {
    reduxProxy.write.emit({ type: GROUP_ACTIONS.GET_GROUPS });
    try {
      const result = await client.get<{ results: Group[]; paging: unknown }>(
        '/groups/resource_types',
        { token: true }
      );
      reduxProxy.write.emit({ type: GROUP_ACTIONS.GET_GROUPS_SUCCESS, result });
    } catch (error) {
      reduxProxy.write.emit({
        type: GROUP_ACTIONS.GET_GROUPS_FAILURE,
        error: ensureError(error)
      });
    }
  }

  function loadGroups() {
    if (!inFlight) {
      inFlight = load().finally(() => {
        inFlight = undefined;
      });
    }
    return inFlight;
  }

  services.messageService.reportSystemEffect.subscribeToRequest(
    async message => {
      if (
        message.request.scope.some(effect => effect.entity === 'resourceGroup')
      ) {
        // A read started before the mutation may still contain the old data.
        await inFlight;
        await loadGroups();
      }
      message.sendResponse();
    }
  );

  function useSnapshot(enabled: boolean): RemoteData<GroupsSnapshot> {
    const state = useGroupsSnapshot();
    useEffect(() => {
      if (enabled && !state.fetched && !state.fetching && !state.fetchError) {
        void loadGroups();
      }
    }, [enabled, state.fetched, state.fetching, state.fetchError]);

    if (!enabled) return rd.ofIdle();
    if (state.fetching) return rd.ofPending();
    if (state.fetchError) {
      return rd.ofError(
        state.fetchError instanceof Error
          ? state.fetchError
          : new Error('Could not load groups')
      );
    }
    if (!state.fetched) return rd.ofPending();
    return rd.of(state);
  }

  return {
    useGroups: query => {
      const snapshot = useSnapshot(maybe.isPresent(query));
      const resourceTypes = useResourceTypes();
      const resourceTypeIdToModel = useMemo(
        () => resourceTypeIdToModelMap(resourceTypes),
        [resourceTypes]
      );

      const data = rd.tryGet(snapshot)?.data;
      const filteredGroups = useMemo(
        () =>
          Object.values(data ?? {})
            .filter(
              group =>
                maybe.isPresent(query) &&
                matchesQuery(query, group, resourceTypeIdToModel)
            )
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id),
        [data, query, resourceTypeIdToModel]
      );
      return rd.map(snapshot, () => filteredGroups);
    },

    useGroup: id => {
      const snapshot = useSnapshot(maybe.isPresent(id));
      return rd.map(snapshot, state =>
        maybe.getOrThrow(
          maybe.isPresent(id) ? state.data[id] : undefined,
          `Group ${id} not found`
        )
      );
    }
  };
}
